import moment from 'moment'
import styled from 'styled-components'
import { FiDownload } from 'react-icons/fi'
import PageHeader from '../../admin/components/PageHeader'
import { getPlayerNameFromCookie } from '../store/MetaStore'

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 10px;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`

const SectionTitle = styled.h3`
  margin-top: 0px;
  margin-bottom: 0px;
  color: #333;
  font-size: 1.2rem;
`

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #060;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #040;
  }
`

const PrizeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);

  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background-color: #f8f9fa;
    color: #444;
    font-weight: bold;
    text-transform: uppercase;
    font-size: 0.85rem;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  background: white;
  border-radius: 8px;
  border: 1px solid #eee;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`

const getPrizeTypeName = (type) => {
  const types = ['Low Net', 'Pinshot', 'League Champ', 'Skin', 'Other']
  return types[type] || 'Unknown'
}

const escapeCsvCell = (val) => {
  if (val === null || val === undefined) return '""'
  const str = String(val)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

const PrizeBoardView = ({ prizes, events, loading, prizeSummary, currentYear }) => {
  const targetYear = currentYear || new Date().getFullYear()

  const getEventString = (eventId) => {
    if (!events || !events.length) return 'Unknown Event'
    const evt = events.find(e => e.eventId === eventId)
    if (!evt) return 'Unknown Event'
    return `${moment(evt.eventDate).format('YYYY-MM-DD')} - ${evt.courseName}`
  }

  const handleDownloadCSV = () => {
    if (!events || !prizes) return

    const currentYearEventIds = new Set(
      events
        .filter(e => e.eventDate && moment(e.eventDate).year() === targetYear)
        .map(e => e.eventId)
    )

    const seasonPrizes = prizes.filter(p => currentYearEventIds.has(p.eventId))

    const sortedSeasonPrizes = [...seasonPrizes].sort((a, b) => {
      const eventA = events?.find(e => e.eventId === a.eventId)
      const eventB = events?.find(e => e.eventId === b.eventId)
      const dateA = eventA ? eventA.eventDate : ''
      const dateB = eventB ? eventB.eventDate : ''

      if (dateA !== dateB) {
        return dateB.localeCompare(dateA)
      }

      const typeNameA = getPrizeTypeName(a.prizeType)
      const typeNameB = getPrizeTypeName(b.prizeType)
      return typeNameB.localeCompare(typeNameA)
    })

    const headers = ['Round Date', 'Course Name', 'Golfer Name', 'Type', 'Amount']
    const csvRows = [headers.join(',')]

    sortedSeasonPrizes.forEach(prize => {
      const evt = events.find(e => e.eventId === prize.eventId)
      const roundDate = evt && evt.eventDate ? moment(evt.eventDate).format('YYYY-MM-DD') : ''
      const courseName = evt?.courseName || ''
      const golferName = prize.golferName || ''
      const type = getPrizeTypeName(prize.prizeType)
      const amount = parseFloat(prize.prizeMoney || 0).toFixed(2)

      const row = [
        escapeCsvCell(roundDate),
        escapeCsvCell(courseName),
        escapeCsvCell(golferName),
        escapeCsvCell(type),
        escapeCsvCell(amount)
      ].join(',')

      csvRows.push(row)
    })

    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `season_prizes_${targetYear}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const pageName = getPlayerNameFromCookie() || 'Prizes'

  return (
    <>
      <PageHeader pageName={pageName} backTo="/league/menu" />
      <Container>
        <div className="animate-fade-in">
          {loading ? (
            <LoadingState>Loading prizes...</LoadingState>
          ) : prizes && prizes.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              {prizeSummary && prizeSummary.length > 0 && (
                <>
                  <SectionTitle style={{ marginBottom: '15px' }}>Season Totals</SectionTitle>
                  <PrizeTable style={{ marginBottom: '30px' }}>
                    <tbody>
                      {prizeSummary.map((summary) => (
                        <tr key={summary.golferId}>
                          <td style={{ fontWeight: '500' }}>{summary.golferName}</td>
                          <td style={{ textAlign: 'right', color: '#060', fontWeight: 'bold' }}>
                            ${parseFloat(summary.totalWinnings).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </PrizeTable>
                </>
              )}

              {prizes.length > 0 && (
                <SectionHeader style={{ marginTop: prizeSummary && prizeSummary.length > 0 ? '0px' : '10px' }}>
                  <SectionTitle>Season Prizes</SectionTitle>
                  <DownloadButton onClick={handleDownloadCSV} title="Download Season Prizes as CSV">
                    <FiDownload /> Download CSV
                  </DownloadButton>
                </SectionHeader>
              )}
              <PrizeTable>
                <tbody>
                  {(() => {
                    const eventIndexMap = {}
                    let currentEventIndex = -1
                    let lastEventId = null

                    const sortedPrizes = [...prizes].sort((a, b) => {
                      const eventA = events?.find(e => e.eventId === a.eventId)
                      const eventB = events?.find(e => e.eventId === b.eventId)
                      const dateA = eventA ? eventA.eventDate : ''
                      const dateB = eventB ? eventB.eventDate : ''

                      if (dateA !== dateB) {
                        return dateB.localeCompare(dateA) // Descending date
                      }

                      const typeNameA = getPrizeTypeName(a.prizeType)
                      const typeNameB = getPrizeTypeName(b.prizeType)
                      return typeNameB.localeCompare(typeNameA) // Descending alpha
                    })

                    return sortedPrizes.map((prize) => {
                      if (prize.eventId !== lastEventId) {
                        if (eventIndexMap[prize.eventId] === undefined) {
                          currentEventIndex++
                          eventIndexMap[prize.eventId] = currentEventIndex
                        }
                        lastEventId = prize.eventId
                      }

                      const index = eventIndexMap[prize.eventId]
                      const bgColor = index % 2 === 0 ? '#f9f9f9' : 'white'

                      return (
                        <tr key={prize.prizeId || prize.id || `${prize.roundId}-${prize.prizeType}`} style={{ backgroundColor: bgColor }}>
                          <td>
                            <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '2px' }}>{getEventString(prize.eventId)}</div>
                            <div style={{ fontWeight: 'bold' }}>{prize.golferName}</div>
                          </td>
                          <td>{getPrizeTypeName(prize.prizeType)}</td>
                          <td style={{ textAlign: 'right', color: '#060', fontWeight: 'bold' }}>
                            ${parseFloat(prize.prizeMoney).toFixed(2)}
                          </td>
                        </tr>
                      )
                    })
                  })()}
                </tbody>
              </PrizeTable>
            </div>
          ) : (
            <EmptyState>
              No prizes have been awarded in this league yet.
            </EmptyState>
          )}
        </div>
      </Container>
    </>
  )
}

export default PrizeBoardView
